/*
  AQUON Bundle Reward Engine
  ==========================
  Single source of truth for the "Unlock Freebies" system.

  Guarantees
  ----------
  - Gifts ACCUMULATE per bundle purchased. Every Select Bundle line in the cart
    earns its own tier's gifts, multiplied by that line's quantity:
      Pack of 1 -> Variety Pack of 8
      Pack of 2 -> AQUON Glass Bottle
      Pack of 3 -> AQUON Glass Bottle (+ the free flavour Pack of 24, see below)
    So 1 Pack added twice earns 2 Variety Packs, and a cart holding a Pack of 1
    and a Pack of 2 keeps BOTH gifts. Adding a bundle never removes the gift an
    earlier bundle already earned.
  - The entitlement is recalculated from the cart on every pass, so it falls as
    well as rises: remove a bundle (or reduce its quantity) and exactly that
    bundle's gifts go with it. Gifts can never outlive the purchase that earned
    them, so a shopper cannot bank a gift and delete the bundle.
  - Free gifts stay at zero cost via a Shopify discount that zeroes the gift
    lines; only the purchased Pack-of-24 lines contribute to the subtotal/total.
  - ZERO-FLICKER swaps (the whole point). See below.

  How zero-flicker works (root-cause fix, not an animation mask)
  --------------------------------------------------------------
  The theme's cart-items.js changes a line with
      fetch('/cart/change.js', { body: { id, quantity, sections } })
  and then morphs the `sections` from THAT response. A naive reconciler runs
  AFTER that morph, so the drawer renders the OLD gift first and the NEW gift a
  moment later -> the visible blink + empty state.

  Instead we wrap `fetch`. When the theme mutates a cart line we:
    1. let the real mutation land (via the ORIGINAL fetch, so we never re-enter),
    2. silently reconcile the gift (add the new one, remove the old one),
    3. re-render the SAME sections the theme asked for, against the now-final cart,
    4. hand the theme a response whose `sections` already show the correct gift+tier.
  The theme morphs ONCE, straight to the final state. One mutation in, one render
  out. The shopper can never see the swap because there is no intermediate render.

  Config: window.AquonBundleFreebies (snippets/bundle-freebies-config.liquid).
*/
(function () {
  'use strict';

  var BF = window.AquonBundleFreebies;
  if (!BF || BF.auto === false || !Array.isArray(BF.tiers)) return;

  // Each tier is one pre-priced bundle variant and the gifts it unlocks. The
  // shopper's tier is whichever bundle variant is in the cart, so a tier is
  // matched by identity, not by counting packs. Ascending, highest match wins.
  var TIERS = BF.tiers
    .map(function (t) {
      var vs = Array.isArray(t.gifts) ? t.gifts : (t.gifts != null ? [t.gifts] : []);
      return {
        tier: parseInt(t.tier, 10) || 0,
        bundle: String(t.bundle || ''),
        variants: vs.map(String).filter(Boolean),
        flavorPack: !!t.flavorPack,
      };
    })
    .filter(function (t) { return t.bundle && t.variants.length; })
    .sort(function (a, b) { return a.tier - b.tier; });
  if (!TIERS.length) return;

  // The Free Pack of 24's flavour variants (one ₹0 variant per flavour). The
  // shopper's 4th pick decides which one the section adds, so the engine can't
  // pick it - it only trims a surplus, never adds (see reconcilePass).
  var FREE_PACK = (BF.freePackVariants || []).map(String).filter(Boolean);

  var GIFT_PROPS = { _free_gift: 'aquon-tier', _gift: 'true', _aquon_auto_added: 'true' };

  var origFetch = window.fetch.bind(window);
  // Only reshape in-place quantity edits (change/update); add/clear are reconciled
  // but keep their native response shape so add-to-cart contracts stay intact.
  var CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  var RESHAPE = /\/cart\/(change|update)(\.js)?(\?|$)/;

  /* ------------------------------------------------------------------ helpers */
  function getCart() {
    return origFetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.json(); });
  }
  function post(url, body) {
    return origFetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) { return r.json().catch(function () { return {}; }); });
  }

  function isGift(i) {
    return !!(i.properties && (i.properties._free_gift || i.properties._gift === 'true' || i.properties._gift === true));
  }
  // The highest tier present. Informational only (kept for the public API and
  // any surface that wants to badge the cart); gifts are NOT derived from it.
  function activeTier(cart) {
    var tier = null;
    (cart.items || []).forEach(function (i) {
      if (isGift(i)) return;
      TIERS.forEach(function (t) {
        if (t.bundle === String(i.variant_id) && (!tier || t.tier > tier.tier)) tier = t;
      });
    });
    return tier;
  }

  // What the cart has EARNED: every bundle line contributes its tier's gifts,
  // multiplied by that line's quantity, summed across all lines. Two Pack-of-1
  // lines (or one line at quantity 2) earn two Variety Packs; a Pack of 1 plus a
  // Pack of 2 earns one of each.
  //
  // `flavorSlots` counts the tier-3 free flavour packs earned. It is tracked
  // apart from `want` because only the shopper's 4th pick decides WHICH flavour
  // variant it is - the engine can trim an excess but must never invent one.
  function entitlements(cart) {
    var want = {};
    var flavorSlots = 0;
    (cart.items || []).forEach(function (i) {
      if (isGift(i)) return;
      var vid = String(i.variant_id);
      var qty = i.quantity || 0;
      if (qty <= 0) return;
      TIERS.forEach(function (t) {
        if (t.bundle !== vid) return;
        t.variants.forEach(function (v) { want[v] = (want[v] || 0) + qty; });
        if (t.flavorPack) flavorSlots += qty;
      });
    });
    return { want: want, flavorSlots: flavorSlots };
  }

  // Back-compat shim: the gift variants the cart should hold, ignoring quantity.
  function activeVariants(cart) {
    return Object.keys(entitlements(cart).want);
  }
  // EVERY gift-flagged line, not just the ones we currently hand out. A line only
  // carries these properties because we auto-added it, so if it is not part of the
  // active tier it is stale and must go. That includes gifts added by an older
  // build of this engine, which pointed at FULL-PRICE variants and leaned on a
  // discount to zero them: a cart made before that changed still holds a ₹600
  // bottle and a ₹999 pack, and the shopper is charged for both. Scoping this to
  // the gifts we currently hand out would leave those lines untouched forever.
  function managedGifts(cart) {
    return (cart.items || []).filter(isGift);
  }

  /* ------------------------------------------------------- reconcile (mutex'd)
     Makes the cart hold exactly the active tier's gift. Pure cart mutation, no
     rendering. Serialized so rapid clicks / concurrent surfaces never race,
     double-add, or leave two gifts. Resolves to { changed, cart }. */
  var chain = Promise.resolve();
  function reconcile() {
    var run = chain.then(doReconcile, doReconcile);
    chain = run.then(noop, noop);
    return run;
  }
  function noop() {}

  /* One reconcile pass: perform AT MOST ONE cart mutation that moves the cart
     toward "the managed gifts == the active tier's variant set, each qty 1", and
     report whether it mutated (so the caller can loop). Every pass re-reads the
     cart, so keys are always fresh (the discount reallocates keys on add/remove).
     Resolves to { mutated:true } or { mutated:false, cart }. */
  function reconcilePass() {
    return getCart().then(function (cart) {
      var ent = entitlements(cart);
      var want = ent.want;                         // variant id -> quantity earned
      var mutated = function () { return { mutated: true }; };
      var setQty = function (line, q) {
        return post('/cart/change.js', { id: line.key, quantity: q }).then(mutated);
      };

      // Managed gift lines, split into the fixed gifts the engine owns outright
      // and the tier-3 flavour packs whose variant only the shopper can choose.
      var fixed = {};                              // variant id -> [lines]
      var flavorLines = [];
      managedGifts(cart).forEach(function (g) {
        var v = String(g.variant_id);
        if (FREE_PACK.indexOf(v) !== -1) { flavorLines.push(g); return; }
        (fixed[v] = fixed[v] || []).push(g);
      });

      // One operation per pass, safe order: drop what is not earned, collapse
      // duplicates, correct quantities, then add what is missing.
      var v, lines;
      for (v in fixed) {                           // no longer earned at all
        if (!want[v]) return setQty(fixed[v][0], 0);
      }
      for (v in fixed) {                           // same variant split over lines
        lines = fixed[v];
        if (lines.length > 1) return setQty(lines[1], 0);
      }
      for (v in fixed) {                           // wrong quantity for the entitlement
        if (fixed[v][0].quantity !== want[v]) return setQty(fixed[v][0], want[v]);
      }
      var missing = Object.keys(want).filter(function (id) { return !fixed[id]; });
      if (missing.length) {
        return post('/cart/add.js', {
          items: missing.map(function (id) {
            return { id: parseInt(id, 10), quantity: want[id], properties: GIFT_PROPS };
          })
        }).then(mutated);
      }

      // Flavour packs: trim anything beyond the number of tier-3 bundles earned.
      // Never force-add - only the section knows which flavour was picked.
      var held = 0;
      flavorLines.forEach(function (g) { held += g.quantity; });
      if (held > ent.flavorSlots) {
        var excess = held - ent.flavorSlots;
        var line = flavorLines[flavorLines.length - 1];
        return setQty(line, Math.max(0, line.quantity - excess));
      }

      return { mutated: false, cart: cart };       // already correct
    });
  }

  function doReconcile() {
    var changed = false;
    function loop(budget) {
      if (budget <= 0) return getCart().then(function (c) { return { changed: changed, cart: c }; });
      return reconcilePass().then(function (r) {
        if (!r.mutated) return { changed: changed, cart: r.cart };
        changed = true;
        return loop(budget - 1);
      });
    }
    return loop(8); // cap passes so a config / stock edge case can never loop forever
  }

  /* --------------------------------------------------------- section rendering
     Render the given section ids against the current (reconciled) cart, exactly
     the way the theme fetches them, so the morph is a drop-in. */
  function renderSections(ids, url) {
    var base = (url || window.location.pathname).split('?')[0];
    return Promise.all(ids.map(function (id) {
      return origFetch(base + '?section_id=' + encodeURIComponent(id), { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.text() : ''; })
        .then(function (html) { return [id, html]; })
        .catch(function () { return [id, '']; });
    })).then(function (pairs) {
      var out = {};
      pairs.forEach(function (p) { if (p[1]) out[p[0]] = p[1]; });
      return out;
    });
  }

  function requestedSections(init) {
    try {
      if (init && typeof init.body === 'string') {
        var b = JSON.parse(init.body);
        if (b && b.sections) {
          return {
            ids: String(b.sections).split(',').map(function (s) { return s.trim(); }).filter(Boolean),
            url: b.sections_url
          };
        }
      }
    } catch (e) {}
    return { ids: [], url: null };
  }

  function jsonResponse(obj) {
    return new Response(JSON.stringify(obj), {
      status: 200,
      headers: { 'Content-Type': 'application/json' }
    });
  }

  /* ------------------------------------------------- the fetch interceptor
     One mutation in -> one final render out. */
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    if (!CART_MUTATION.test(url)) return origFetch(input, init);

    return origFetch(input, init).then(function (res) {
      if (!res.ok) return res; // let the theme surface stock/errors unchanged
      return res.clone().json().then(function (body) {
        return reconcile().then(function (result) {
          if (!result.changed) return res;             // gift already correct
          if (!RESHAPE.test(url)) return res;          // add/clear: reconciled, native shape kept
          var req = requestedSections(init);
          if (!req.ids.length) return jsonResponse(result.cart);
          return renderSections(req.ids, req.url).then(function (sections) {
            // Final cart + freshly rendered sections -> theme morphs ONCE.
            return jsonResponse(Object.assign({}, result.cart, { sections: sections }));
          });
        }).catch(function () { return res; });         // any failure -> safe passthrough
      }).catch(function () { return res; });            // non-JSON body -> passthrough
    });
  };

  /* ------------------------------------------------------------- public API */
  window.AquonBundleEngine = {
    reconcile: reconcile,
    activeTier: activeTier,
    activeVariants: activeVariants,
    entitlements: entitlements,
    tiers: TIERS
  };

  /* -------------------------------------------- initial safety reconcile
     The engine keeps the cart correct, but a cart built on another device/tab
     (or before this script existed) might be stale. Fix it on load and, if it
     changed, refresh any cart surfaces already on the page. */
  function refreshPresentSections() {
    var ids = [];
    try {
      var evt = new Event('cart:grouped-sections', { bubbles: true });
      evt.detail = { sections: ids };
      document.dispatchEvent(evt);
    } catch (e) {}
    var unique = ids.filter(function (v, i) { return v && ids.indexOf(v) === i; });
    var present = unique.filter(function (id) { return document.getElementById('shopify-section-' + id); });
    if (!present.length) return;
    renderSections(present).then(function (sections) {
      Object.keys(sections).forEach(function (id) {
        var wrap = document.getElementById('shopify-section-' + id);
        if (!wrap) return;
        try {
          var doc = new DOMParser().parseFromString(sections[id], 'text/html');
          var incoming = doc.getElementById('shopify-section-' + id);
          wrap.innerHTML = incoming ? incoming.innerHTML : sections[id];
        } catch (e) {}
      });
    });
  }
  function initialReconcile() {
    reconcile().then(function (result) { if (result.changed) refreshPresentSections(); }).catch(noop);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialReconcile);
  else initialReconcile();
})();
